import React from 'react';
import Text from '@/components/Text';
import { SlideProps } from './slideTypes';
import { PIE_CHART_COLORS, SLIDE_COMPONENT_TEXT_SIZE, SLIDE_COMPONENT_TITLE_SIZE } from './slideConstants';

export function BarChartSlide({ slide }: SlideProps) {
  const chartTitle = slide.data?.title || slide.data?.data?.title || 'Bar Chart';
  
  // Support both simple and complex grouped formats
  const hasSimpleFormat = Array.isArray(slide.data?.data?.labels) && Array.isArray(slide.data?.data?.values);
  
  let chartData: any[] = [];
  let isSimpleFormat = false;
  
  if (hasSimpleFormat) {
    isSimpleFormat = true;
    const labels = slide.data.data.labels;
    const values = slide.data.data.values;
    chartData = labels.map((label: string, idx: number) => ({
      label,
      value: values[idx] || 0,
    })).filter((item: any) => item.label && (typeof item.value === 'number' || !isNaN(Number(item.value))));
  } else {
    const rawChartData = Array.isArray(slide.data?.chartData) 
      ? slide.data.chartData 
      : Array.isArray(slide.data?.data?.chartData)
        ? slide.data.data.chartData
        : [];
    chartData = rawChartData.filter(
      (item: any) => item && item.label && item.values && typeof item.values === 'object'
    );
  }

  if (chartData.length === 0) return null;

  let categories: string[] = [];
  let maxValue = 0;
  
  if (isSimpleFormat) {
    chartData.forEach((item: any) => {
      const val = typeof item.value === 'number' ? item.value : Number(item.value) || 0;
      if (val > maxValue) maxValue = val;
    });
  } else {
    const allCategories = new Set<string>();
    chartData.forEach((item: any) => {
      Object.keys(item.values).forEach(cat => allCategories.add(cat));
    });
    categories = Array.from(allCategories).sort();
    
    chartData.forEach((item: any) => {
      Object.values(item.values).forEach((val: any) => {
        const numVal = typeof val === 'number' ? val : Number(val) || 0;
        if (numVal > maxValue) maxValue = numVal;
      });
    });
  }

  maxValue = Math.ceil(maxValue * 1.15);
  const yLabels = [0, Math.round(maxValue / 2), Math.round(maxValue)];

  return (
    <div className="flex flex-col gap-4 md:gap-6 w-full px-4 md:px-6 py-6 md:py-8 items-start justify-center overflow-y-auto max-h-full">
      <Text size={SLIDE_COMPONENT_TITLE_SIZE} className="text-white font-normal mb-1 md:mb-2 text-left text-sm md:text-lg">{chartTitle}</Text>
      
      <div className="w-full aspect-[16/9] bg-white/5 border border-white/10 rounded-2xl p-4 md:p-8 flex flex-col backdrop-blur-sm">
        {/* Chart Area */}
        <div className="flex-1 w-full relative flex flex-col pt-4 overflow-hidden">
          {/* Y-Axis Grid Lines */}
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none pr-2 pb-8 md:pb-10">
            {yLabels.reverse().map((val, i) => (
              <div key={i} className="flex items-center gap-2 md:gap-4 w-full">
                <div className="w-8 md:w-12 text-right">
                  <Text size="xs" className="text-white/30 font-normal text-[8px] md:text-xs">{val.toLocaleString()}</Text>
                </div>
                <div className="flex-1 h-px bg-white/5" />
              </div>
            ))}
          </div>

          {/* Bars Area */}
          <div className="flex-1 flex items-end ml-10 md:ml-16 mr-2 pb-8 md:pb-10">
            {isSimpleFormat ? (
              <div className="w-full h-full flex items-end justify-around gap-1 md:gap-2">
                {chartData.map((item, idx) => {
                  const h = (item.value / maxValue) * 100;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center group max-w-[40px] md:max-w-[60px] relative">
                  <div 
                        className="w-full rounded-t-sm md:rounded-t-lg transition-all duration-1000 ease-out shadow-lg"
                    style={{ 
                          height: `${h}%`,
                          backgroundColor: PIE_CHART_COLORS[idx % PIE_CHART_COLORS.length]
                    }} 
                  />
                      <div className="absolute -bottom-6 md:-bottom-8 w-full text-center">
                        <Text size="xs" className="text-white/60 font-normal line-clamp-1 md:line-clamp-2 text-[8px] md:text-xs">{item.label}</Text>
                </div>
              </div>
            );
          })}
        </div>
            ) : (
              <div className="w-full h-full flex items-end justify-around gap-4 md:gap-8">
                {categories.map((cat, catIdx) => (
                  <div key={catIdx} className="flex-1 flex flex-col items-center h-full relative group">
                    <div className="w-full h-full flex items-end justify-center gap-0.5 md:gap-1">
                      {chartData.map((item, itemIdx) => {
                        const val = item.values[cat] || 0;
                        const h = (val / maxValue) * 100;
                        return (
                          <div 
                            key={itemIdx}
                            className="flex-1 rounded-t-[1px] md:rounded-t-md transition-all duration-1000 ease-out shadow-md min-w-[4px] md:min-w-[8px]"
                            style={{ 
                              height: `${h}%`,
                              backgroundColor: PIE_CHART_COLORS[itemIdx % PIE_CHART_COLORS.length]
                            }}
                          />
                        );
                      })}
                    </div>
                    <div className="absolute -bottom-6 md:-bottom-8 w-full text-center">
                      <Text size="xs" className="text-white/60 font-normal whitespace-nowrap text-[8px] md:text-xs">{cat}</Text>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Legend for complex format */}
        {!isSimpleFormat && (
          <div className="flex flex-wrap gap-x-3 md:gap-x-6 gap-y-1 md:gap-y-2 justify-center mt-2 md:mt-4">
            {chartData.map((item, idx) => (
              <div key={idx} className="flex items-center gap-1.5 md:gap-2">
                <div 
                  className="w-2 h-2 md:w-3 md:h-3 rounded-sm"
                  style={{ backgroundColor: PIE_CHART_COLORS[idx % PIE_CHART_COLORS.length] }}
                />
                <Text size="xs" className="text-white/80 font-normal text-[8px] md:text-xs">{item.label}</Text>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
